import { useContext, useState } from "react"
import { Alert } from "@components"
import { NotificationContext } from "@context"
import { GET_EVENT_GUARDIANS_STAFF, IMPERSONATE_MUTATION } from "@graphql"
import { getGraphQLErrorMessage } from "@helpers"
import { useImpersonateUser } from "@hooks"

import { 
  Autocomplete,
  AutocompleteItem,
  Button,
  RadioGroup, 
  Radio,
} from "@heroui/react"

import { useMutation, useQuery } from "@apollo/client"

import PropTypes from "prop-types"

const ImpersonateUserForm = ({ clientId, event }) => {
  const [guardians, setGuardians] = useState([])
  const [staff, setStaff] = useState([])
  const [perPage] = useState(3000) // using this to avoid infinite scroll issues
  const [selectedGuardian, setSelectedGuardian] = useState(null)
  const [selectedStaffMember, setSelectedStaffMember] = useState(null)
  const [impersonationType, setImpersonationType] = useState("PARENTS")
  const { login } = useImpersonateUser(event.id);
  const { notify } = useContext(NotificationContext)

  const [impersonate, { loading }] = useMutation(IMPERSONATE_MUTATION, {
    onCompleted: (response) => login(impersonationType, response.impersonate.token, event.id),
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "Error occurred while attempting to impersonate a user."), open: true })
  })

  const guardiansStaffQuery = useQuery(GET_EVENT_GUARDIANS_STAFF, {
    variables: {
      id: event.id,
      page: 1,
      perPage,
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    
    onCompleted: (data) => {
      setGuardians(data.event.guardians.data)
      setStaff(data.event.staff.data)
    },

    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "Error occurred while fetching guardians and staff."), open: true })
  })

  const handleImpersonate = () => {
    impersonate({
      variables: {
        input: {
          user_id: impersonationType === "PARENTS" ? selectedGuardian.user_id : selectedStaffMember.user_id,
          client_id: clientId,
          type: impersonationType,
        }
      }
    })
  }
  
  const handleImpersonationTypeChange = (value) => {
    (value === "PARENTS") ? setSelectedStaffMember(null) : setSelectedGuardian(null)
    
    setImpersonationType(value)
  }

  const handleSelectGuardian = (selectedOption) => {
    setSelectedGuardian(guardians.find(guardian => guardian.user_id === selectedOption))
  }

  const handleSelectStaffMember = (selectedOption) => {
    setSelectedStaffMember(staff.find(staffMember => staffMember.user_id === selectedOption))
  }

  const isDisabled = selectedGuardian === null && selectedStaffMember === null

  return (
    <div>      
      {!event.open_staff && (
        <Alert type="info" classNames="mb-4" title="Staff Impersonation Disabled" description="Impersonating staff is disabled as this event is currently closed to staff." />
      )}
      
      <p className="mb-4">Select a guardian or staff member to impersonate. Only guardians and staff members involved in this event can be selected.</p>

      <RadioGroup 
        className="mb-3"
        onValueChange={(value) => handleImpersonationTypeChange(value)} 
        value={impersonationType}
      >
        <Radio value="PARENTS">Login as a guardian</Radio>
        <Radio value="STAFF" isDisabled={!event.open_staff}>Login as a staff member</Radio>
      </RadioGroup>

      {impersonationType === "PARENTS" && (
        <Autocomplete
          defaultItems={guardians}
          onSelectionChange={(selectedOption) => handleSelectGuardian(selectedOption)}
          isLoading={guardiansStaffQuery.loading}
          placeholder="Search for a guardian"
          aria-label="Search for a guardian"
        >
          {guardians.map((guardian) => (
            <AutocompleteItem key={guardian.user_id} aria-label={`${guardian.forename} ${guardian.surname}`}>
              {`${guardian.forename} ${guardian.surname}`}
            </AutocompleteItem>
          ))}
        </Autocomplete>
      )}

      {impersonationType === "STAFF" && (
        <Autocomplete
          defaultItems={staff}
          onSelectionChange={(selectedOption) => handleSelectStaffMember(selectedOption)}
          isLoading={guardiansStaffQuery.loading}
          placeholder="Search for a member of staff"
          aria-label="Search for a member of staff"
        >
          {staff.map((staffMember) => (
            <AutocompleteItem key={staffMember.user_id} aria-label={`${staffMember.forename} ${staffMember.surname}`}>
              {`${staffMember.forename} ${staffMember.surname}`}
            </AutocompleteItem>
          ))}
        </Autocomplete>
      )}
           
      <div className="flex justify-between gap-3 mt-4">
        <Button color="secondary" onPress={handleImpersonate} tabIndex={-1} isDisabled={isDisabled} isLoading={loading} fullWidth>Login</Button>
      </div>
    </div>
  )
}

ImpersonateUserForm.propTypes = {
  clientId: PropTypes.string.isRequired,
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    open_staff: PropTypes.bool.isRequired,
  }).isRequired,
}

export default ImpersonateUserForm;